import React, { useEffect, useRef, useState } from 'react';
import { Briefcase, Calendar } from 'lucide-react';
import { Card } from './ui/card';

const Experience = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const currentRef = sectionRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const experiences = [
    {
      organization: 'International Islamic University Islamabad (IIUI)',
      role: 'Professor of English Literature',
      period: '2016 – 2023',
      responsibilities: [
        'Led advanced teaching and research in linguistics and literary studies.',
        'Supervised numerous PhD and MPhil theses to successful completion.',
        'Published extensively in HEC-recognized and international journals.'
      ]
    },
    {
      organization: 'International Islamic University Islamabad (IIUI)',
      role: 'Vice President (Academics)',
      period: '2021 – 2023',
      responsibilities: [
        'Oversaw academic policy, quality assurance, and institutional planning.',
        'Led university-wide academic reforms and online teaching initiatives.'
      ]
    },
    {
      organization: 'International Islamic University Islamabad (IIUI)',
      role: 'Dean, Faculty of Languages & Literature',
      period: '2019 – 2021',
      responsibilities: [
        'Managed faculty operations, academic programmes, and research strategy.',
        'Strengthened postgraduate programmes and faculty development initiatives.'
      ]
    },
    {
      organization: 'International Islamic University Islamabad (IIUI)',
      role: 'Chairman, Department of English',
      period: '2015 – 2018',
      responsibilities: [
        'Revitalized curriculum and research culture within the department.',
        'Coordinated MPhil and PhD programmes and external academic reviews.'
      ]
    },
    {
      organization: 'University of Manchester & University of Birmingham (UK)',
      role: 'Postdoctoral Researcher / Research & Teaching Assistant',
      period: 'Various Terms',
      responsibilities: [
        'Conducted advanced research in corpus linguistics and narrative studies.',
        'Contributed to teaching, research projects, and international collaboration.'
      ]
    }
  ];

  return (
    <section id="experience" ref={sectionRef} className="py-16 bg-gradient-to-b from-blue-50 to-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-3">
            Experience
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-cyan-500 mx-auto"></div>
        </div>

        {/* Timeline */}
        <div className="max-w-4xl mx-auto">
          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-blue-200"></div>

            {experiences.map((exp) => (
              <div
                key={`${exp.role}-${exp.period}`}
                className={`relative mb-8 transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
                }`}
              >
                <Card className="md:w-[calc(50%-2rem)] md:ml-auto md:pl-8 p-5 bg-white border-gray-200 hover:border-blue-600 hover:shadow-lg transition-all duration-300">
                  {/* Timeline Dot */}
                  <div className="hidden md:block absolute left-1/2 top-6 transform -translate-x-1/2 w-3 h-3 bg-blue-600 rounded-full border-4 border-white shadow-md"></div>

                  {/* Mobile Icon */}
                  <div className="md:hidden flex items-center mb-3">
                    <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                      <Briefcase className="text-blue-600" size={16} />
                    </div>
                    <div className="flex items-center text-xs text-gray-500">
                      <Calendar size={14} className="mr-1" />
                      {exp.period}
                    </div>
                  </div>

                  {/* Content */}
                  <div>
                    <div className="hidden md:flex items-center text-xs text-gray-500 mb-1.5">
                      <Calendar size={14} className="mr-1" />
                      {exp.period}
                    </div>
                    <h3 className="font-bold text-lg text-gray-900 mb-1">
                      {exp.role}
                    </h3>
                    <p className="text-blue-700 font-medium mb-3 text-sm">
                      {exp.organization}
                    </p>
                    <ul className="space-y-1.5">
                      {exp.responsibilities.map((resp, idx) => (
                        <li key={idx} className="text-gray-600 text-xs leading-relaxed flex">
                          <span className="text-blue-600 mr-2">•</span>
                          <span>{resp}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;
